import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Palette, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import api, { formatApiError } from "../../api";
import { useBranding } from "../../context/BrandingContext";
import { Field, Input, Select } from "../common";
import BrandPreview from "../BrandPreview";
import BrandDiscoveryPanel from "../BrandDiscoveryPanel";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";
import { useSectionForm } from "./useSectionForm";

/**
 * Per-instance branding: the white-label card.
 *
 * Saves a **partial** update — only the fields rendered here — so it can never
 * clobber a value set from a `BRAND_*` variable that has no box on this form.
 * Clearing a required box (name, wordmark, title) restores the environment
 * value or the product default rather than blanking it, which is why the hint
 * says so out loud: an admin needs to know there is a way back.
 *
 * Layout note: at `xl` the preview becomes a sticky right-hand column, because
 * the whole point of a live preview is to be visible *while* you edit the
 * fields it previews, and it was previously scrolled off the top long before
 * you reached the colour pickers. Below `xl` it sits inline under the discovery
 * panel. Grid row/column placement keeps that to a single mounted instance
 * rather than one visible and one hidden copy.
 */

// The five uploadable slots, each paired with the branding field an upload
// fills in. Mirrors branding_assets.KIND_TO_FIELD on the backend.
const ASSET_SLOTS = [
  { kind: "logo_icon",        field: "logo_icon_url",        labelKey: "branding.logoIconUrl",        placeholder: "/logo_icon.png" },
  { kind: "logo",             field: "logo_url",             labelKey: "branding.logoUrl",            placeholder: "/logo.png" },
  { kind: "favicon",          field: "favicon_url",          labelKey: "branding.faviconUrl",         placeholder: "/favicon.ico" },
  { kind: "apple_touch_icon", field: "apple_touch_icon_url", labelKey: "branding.appleTouchIconUrl",  placeholder: "/apple-touch-icon.png" },
  { kind: "login_image",      field: "login_image_url",      labelKey: "branding.loginImageUrl",      placeholder: "" },
];

const COLOR_FIELDS = [
  ["primary", "branding.primary"],
  ["primary_hover", "branding.primaryHover"],
  ["glow", "branding.glow"],
  ["theme_color", "branding.themeColor"],
];

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/x-icon,image/vnd.microsoft.icon";

export default function BrandingSettings() {
  const { t } = useTranslation();
  const { setBranding: applyBranding } = useBranding();
  const [assets, setAssets] = useState(null);
  const [overlay, setOverlay] = useState({});
  const [assetError, setAssetError] = useState("");

  const section = useSectionForm({
    path: "/settings/branding",
    // Push the saved record into the app-wide context so the sidebar wordmark,
    // theme colours and browser tab update immediately. Without this an admin
    // has to reload to find out whether their own change worked.
    onSaved: applyBranding,
  });

  const loadAssets = useCallback(() => {
    api.get("/settings/branding/assets").then((r) => setAssets(r.data))
      .catch((e) => setAssetError(formatApiError(e.response?.data?.detail) || e.message));
  }, []);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  const setBrandList = (key) => (e) =>
    section.setForm((prev) => ({
      ...prev,
      // Comma-separated in the box, an array over the wire. Trailing empties
      // are dropped so "EUR, " mid-typing doesn't fail the three-letter check
      // on every keystroke -- validation happens on save, not per character.
      [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
    }));

  const uploadAsset = async (kind, field, file) => {
    setAssetError("");
    const body = new FormData();
    body.append("file", file);
    try {
      // No explicit Content-Type: axios has to set the multipart boundary
      // itself, and overriding the header drops it.
      const r = await api.post(`/settings/branding/assets/${kind}`, body);
      // Only the uploaded slot's URL is taken from the response, so an
      // in-progress edit to another field survives the upload.
      section.adoptFields(r.data, [field]);
      applyBranding(r.data);
      loadAssets();
    } catch (e) {
      console.error("Branding asset upload failed:", e);
      // Verbatim on purpose: the backend explains *why* (an SVG can carry
      // scripts; the file is over this slot's limit), and a generic "upload
      // failed" would waste the admin's time.
      setAssetError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeAsset = async (kind, field) => {
    setAssetError("");
    try {
      const r = await api.delete(`/settings/branding/assets/${kind}`);
      section.adoptFields(r.data, [field]);
      applyBranding(r.data);
      loadAssets();
    } catch (e) {
      console.error("Branding asset delete failed:", e);
      setAssetError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const form = section.form;
  const previewRecord = form ? { ...form, ...overlay } : null;

  return (
    <SectionShell
      icon={Palette} tone="primary"
      title={t("branding.title")} desc={t("branding.desc")}
      error={section.error || assetError}
      dirty={section.dirty} saving={section.saving} saved={section.saved}
      onSave={form ? section.save : undefined} onDiscard={section.discard}>

      {!form ? (
        <p className="text-sm text-muted mt-5">{t("common.loading")}</p>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-8 xl:items-start">

          {/* Discovery first: "paste your website" is the fast path, and the
              fields below are what you reach for to correct it. */}
          <div className="min-w-0 pt-5 xl:col-start-1 xl:row-start-1">
            <BrandDiscoveryPanel
              current={form}
              onOverlayChange={setOverlay}
              onError={section.setError}
              onApplied={(record) => {
                section.adopt(record);
                applyBranding(record);
                loadAssets();
              }} />
          </div>

          {/* Sticky beside the form at xl, inline under the discovery panel
              below it. Row/column placement rather than two copies. */}
          <aside className="min-w-0 xl:col-start-2 xl:row-start-1 xl:row-span-2 xl:sticky xl:top-6 xl:pt-5">
            <BrandPreview record={previewRecord} />
          </aside>

          <div className="min-w-0 xl:col-start-1 xl:row-start-2">
            <FieldGroups>
              <FieldGroup label={t("branding.identityGroup")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("branding.orgName")}>
                    <Input data-testid="branding-org-name" value={form.org_name}
                      onChange={section.patch("org_name")} />
                  </Field>
                  <Field label={t("branding.tagline")}>
                    <Input data-testid="branding-tagline" value={form.tagline}
                      onChange={section.patch("tagline")}
                      placeholder={t("branding.taglinePlaceholder")} />
                  </Field>
                  <Field label={t("branding.wordmarkText")}>
                    <Input data-testid="branding-wordmark-text" value={form.wordmark_text}
                      onChange={section.patch("wordmark_text")} />
                  </Field>
                  <Field label={t("branding.wordmarkAccent")}>
                    <Input data-testid="branding-wordmark-accent" value={form.wordmark_accent}
                      onChange={section.patch("wordmark_accent")} placeholder=".io" />
                  </Field>
                </div>
              </FieldGroup>

              <FieldGroup label={t("branding.metadataGroup")} hint={t("branding.metadataHint")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("branding.documentTitle")}>
                    <Input data-testid="branding-document-title" value={form.document_title}
                      onChange={section.patch("document_title")} />
                  </Field>
                  <Field label={t("branding.metaDescription")}>
                    <Input data-testid="branding-meta-description" value={form.meta_description}
                      onChange={section.patch("meta_description")} />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={t("branding.orgDescription")}>
                      <Input data-testid="branding-org-description" value={form.org_description}
                        onChange={section.patch("org_description")}
                        placeholder={t("branding.orgDescriptionPlaceholder")} />
                    </Field>
                  </div>
                </div>
              </FieldGroup>

              <FieldGroup label={t("branding.coloursGroup")} hint={t("branding.coloursHint")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {COLOR_FIELDS.map(([key, labelKey]) => (
                    <Field key={key} label={t(labelKey)}>
                      <div className="flex items-center gap-2">
                        {/* A picker and a text box over one value: the picker
                            for choosing, the box for pasting the exact hex a
                            brand guide hands over. */}
                        <input type="color" aria-label={t(labelKey)}
                          value={form[key]} onChange={section.patch(key)}
                          className="w-10 h-9 rounded-sm border border-border bg-surface shrink-0 cursor-pointer" />
                        <Input data-testid={`branding-${key}`} value={form[key]}
                          onChange={section.patch(key)} className="font-mono" />
                      </div>
                    </Field>
                  ))}
                </div>
              </FieldGroup>

              {/* Each slot keeps its URL box *and* gets an upload: the field
                  means "where this image comes from", and uploading is a
                  shortcut that fills it in with a route on this server. */}
              <FieldGroup label={t("branding.images")} hint={t("branding.imagesHint")}>
                {ASSET_SLOTS.map(({ kind, field, labelKey, placeholder }) => {
                  const uploaded = (assets || []).find((a) => a.kind === kind);
                  const src = form[field];
                  return (
                    <div key={kind} className="flex items-center gap-3">
                      {/* Neutral tile: a transparent PNG on a plain background
                          is invisible, which reads as a failed upload. */}
                      <div className="w-11 h-11 shrink-0 rounded-sm border border-border bg-surface flex items-center justify-center overflow-hidden">
                        {src
                          ? <img src={src} alt="" className="max-w-full max-h-full object-contain" />
                          : <ImageIcon size={16} className="text-muted" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <label className="block text-xs text-muted mb-1">{t(labelKey)}</label>
                        <Input data-testid={`branding-${kind}-url`} value={src}
                          onChange={section.patch(field)} placeholder={placeholder} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0 self-end">
                        <label data-testid={`branding-upload-${kind}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-sm text-xs text-muted border border-border hover:bg-surface hover:text-txt cursor-pointer transition-colors">
                          <Upload size={14} />
                          <span className="hidden sm:inline">{t("branding.upload")}</span>
                          <input type="file" className="hidden" accept={ACCEPT}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              // Reset so re-picking the same file after a
                              // rejection still fires onChange.
                              e.target.value = "";
                              if (file) uploadAsset(kind, field, file);
                            }} />
                        </label>
                        {uploaded && (
                          <button onClick={() => removeAsset(kind, field)}
                            data-testid={`branding-remove-${kind}`} title={t("branding.removeUpload")}
                            className="p-2 rounded-sm text-muted border border-border hover:text-danger hover:bg-danger/10 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </FieldGroup>

              <FieldGroup label={t("branding.domainsGroup")} hint={t("branding.domainsHint")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("branding.internalDomains")}>
                    <Input data-testid="branding-internal-domains"
                      value={(form.internal_domains || []).join(", ")}
                      onChange={setBrandList("internal_domains")} placeholder="acme.io, acme.co.uk" />
                  </Field>
                  <Field label={t("branding.googleHd")}>
                    <Input data-testid="branding-google-hd" value={form.google_hd}
                      onChange={section.patch("google_hd")} placeholder="acme.io" />
                  </Field>
                </div>
              </FieldGroup>

              <FieldGroup label={t("branding.moneyGroup")} hint={t("branding.moneyHint")}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("branding.currencies")}>
                    <Input data-testid="branding-currencies"
                      value={(form.currencies || []).join(", ")}
                      onChange={setBrandList("currencies")} placeholder="EUR, HUF, USD" />
                  </Field>
                  <Field label={t("branding.defaultCurrency")}>
                    <Select data-testid="branding-default-currency" value={form.default_currency}
                      onChange={section.patch("default_currency")}>
                      {(form.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                  </Field>
                </div>
              </FieldGroup>

              <FieldGroup>
                <p className="text-xs text-muted">{t("branding.hint")}</p>
              </FieldGroup>
            </FieldGroups>
          </div>
        </div>
      )}
    </SectionShell>
  );
}
